/**
 * Component Settings Panel
 * 
 * Settings panel for individual component configuration
 * 
 * @package TopBuddy
 */

import { useState, useEffect, useRef } from '@wordpress/element';
import { TabPanel, TextControl, TextareaControl, SelectControl, ToggleControl, RadioControl, ColorPicker, Dropdown, ColorIndicator, Button, BoxControl, DateTimePicker } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import ProBadge from '../ProBadge';

/**
 * TinyMCE Editor Component
 */
const TinyMCEEditor = ({ editorId, value, onChange, label }) => {
	const editorRef = useRef(null);
	const onChangeRef = useRef(onChange);

	// Keep onChange ref up to date
	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	useEffect(() => {
		const initEditor = () => {
			if (window.wp && window.wp.editor) {
				// Remove existing editor if any
				if (window.wp.editor.remove) {
					window.wp.editor.remove(editorId);
				}

				// Initialize new editor
				window.wp.editor.initialize(editorId, {
					tinymce: {
						wpautop: true,
						plugins: 'lists,paste',
						toolbar1: 'bold,italic',
						inline_boundaries: false,
						setup: (editor) => {
							editorRef.current = editor;
							editor.on('change keyup', () => {
								let content = editor.getContent();
								// Replace non-breaking spaces with regular spaces
								content = content.replace(/\u00a0/g, ' ');
								onChangeRef.current(content);
							});
						}
					},
					quicktags: false,
					mediaButtons: false,
				});
			}
		};

		// Delay initialization to ensure textarea is in DOM
		const timer = setTimeout(initEditor, 100);
		
		return () => {
			clearTimeout(timer);
			if (window.wp && window.wp.editor && window.wp.editor.remove) {
				window.wp.editor.remove(editorId);
			}
		};
	}, [editorId]);

	return (
		<div className="topbuddy-settings-field topbuddy-settings-field--tinymce">
			<label className="topbuddy-settings-field__label">{label}</label>
			<textarea
				id={editorId}
				className="topbuddy-tinymce-editor"
				defaultValue={value || ''}
			/>
		</div>
	);
};

const ComponentSettings = ({ component, config, onUpdateSettings, onClose }) => {
	const [settings, setSettings] = useState(component.settings || {});
	const modalRef = useRef(null);

	const postSettingsData = window.topbuddyPostSettings || {};
	const timezoneOffset = parseInt(postSettingsData.timezoneOffset || 0, 10);
	const isProActive = postSettingsData.isProActive || false;
	const upgradeUrl = postSettingsData.upgradeUrl || 'https://wptopbuddy.com/?utm_source=plugin&utm_medium=topbuddy-free-plugin&utm_campaign=topbuddy-free-plugin-admin';

	// Helper to convert Site Time String (from DB) to Local Date Object (for Picker)
	const toLocal = (siteDateString) => {
		if (!siteDateString) return null;
		// Ensure ISO format
		const isoString = siteDateString.replace(' ', 'T');
		// Parse as UTC to get the raw values
		const d = new Date(isoString + 'Z');
		// Adjust for site offset (subtract offset to get UTC timestamp of the represented time)
		const utcTimestamp = d.getTime() - (timezoneOffset * 1000);
		return new Date(utcTimestamp);
	};

	// Helper to convert Local Date String (from Picker) to Site Time String (for DB)
	const toSite = (localDateString) => {
		if (!localDateString) return null;
		const localDate = new Date(localDateString);
		// Get UTC timestamp
		const utcTimestamp = localDate.getTime();
		// Add offset to get Site Time represented as UTC
		const siteTimestamp = utcTimestamp + (timezoneOffset * 1000);
		const siteDate = new Date(siteTimestamp);
		// Format as YYYY-MM-DD HH:mm:ss
		return siteDate.toISOString().slice(0, 19).replace('T', ' ');
	};

	// Update local state when component settings change
	useEffect(() => {
		setSettings(component.settings || {});
	}, [component.settings]);

	// Handle backdrop click to close modal
	const handleBackdropClick = () => {
		onClose();
	};

	// Prevent clicks inside modal content from closing it
	const handleModalClick = (e) => {
		if (e && e.stopPropagation) {
			e.stopPropagation();
		}
	};

	const handleSettingChange = (key, value) => {
		const newSettings = { ...settings, [key]: value };
		setSettings(newSettings);
		onUpdateSettings(newSettings);
	};

	const renderField = (key, field) => {
		const value = settings[key] !== undefined ? settings[key] : field.default;

		// Check if field has conditions
		if (field.condition) {
			let isMet = false;

			// Check if it's the new format with 'field', 'operator', and 'value'
			if (field.condition.field && field.condition.operator !== undefined && field.condition.value !== undefined) {
				const fieldName = field.condition.field;
				const operator = field.condition.operator;
				const expectedValue = field.condition.value;
				const currentValue = settings[fieldName] !== undefined ? settings[fieldName] : (config.settings[fieldName]?.default);

				switch (operator) {
					case '==':
					case '===':
						isMet = currentValue === expectedValue;
						break;
					case '!=':
					case '!==':
						isMet = currentValue !== expectedValue;
						break;
					case 'in':
						isMet = Array.isArray(expectedValue) && expectedValue.includes(currentValue);
						break;
					case 'not_in':
						isMet = Array.isArray(expectedValue) && !expectedValue.includes(currentValue);
						break;
					default:
						isMet = currentValue === expectedValue;
				}
			} else {
				// Legacy format: direct key-value pairs
				isMet = Object.keys(field.condition).every((condKey) => {
					const requiredValue = field.condition[condKey];
					const currentValue = settings[condKey] !== undefined ? settings[condKey] : (config.settings[condKey]?.default);
					
					if (Array.isArray(requiredValue)) {
						return requiredValue.includes(currentValue);
					}
					
					return currentValue === requiredValue;
				});
			}

			if (!isMet) {
				return null;
			}
		}

		switch (field.type) {
			case 'hidden':
				// Hidden fields don't render anything visible
				return null;

			case 'text':
				return (
					<div key={key} className="topbuddy-settings-field">
						<label className="topbuddy-settings-field__label">{field.label}</label>
						<TextControl
							value={value || ''}
							onChange={(val) => handleSettingChange(key, val)}
							className="topbuddy-settings-field__input"
							__next40pxDefaultSize
							__nextHasNoMarginBottom
						/>
					</div>
				);

			case 'shortcode':
				return (
					<div key={key} className="topbuddy-settings-field">
						<label className="topbuddy-settings-field__label">{field.label}</label>
						<TextControl
							value={value || ''}
							onChange={(val) => handleSettingChange(key, val)}
							className="topbuddy-settings-field__input"
							placeholder={__('[shortcode]', 'topbuddy')}
							__next40pxDefaultSize
							__nextHasNoMarginBottom
						/>
					</div>
				);

			case 'url':
				return (
					<div key={key} className="topbuddy-settings-field">
						<label className="topbuddy-settings-field__label">{field.label}</label>
						<TextControl
							type="url"
							value={value || ''}
							onChange={(val) => handleSettingChange(key, val)}
							className="topbuddy-settings-field__input"
							__next40pxDefaultSize
							__nextHasNoMarginBottom
						/>
					</div>
				);

		case 'textarea':
			const editorId = `topbuddy-editor-${key}-${component.id}`;
			
			return (
				<TinyMCEEditor
					key={key}
					editorId={editorId}
					value={value}
					onChange={(val) => handleSettingChange(key, val)}
					label={field.label}
				/>
			);

		case 'textarea_control':
			const displayValue = String(value || '')
				.replace(/\\r\\n/g, '\n')
				.replace(/\\n/g, '\n')
				.replace(/\\r/g, '\n');

			return (
				<div key={key} className="topbuddy-settings-field">
					<label className="topbuddy-settings-field__label">{field.label}</label>
					{field.description && (
						<span className="topbuddy-settings-field__description">{field.description}</span>
					)}
					<TextareaControl
						value={displayValue}
						onChange={(val) => handleSettingChange(key, val)}
						rows={6}
						__nextHasNoMarginBottom
					/>
				</div>
			);

		case 'number':
			return (
				<div key={key} className="topbuddy-settings-field">
					<label className="topbuddy-settings-field__label">{field.label}</label>
					<TextControl
						type="number"
						value={value ?? field.default ?? ''}
						onChange={(val) => {
							// TextControl returns a string; store as number when possible.
							if (val === '' || val === null || val === undefined) {
								handleSettingChange(key, '');
								return;
							}
							const parsed = Number(val);
							handleSettingChange(key, Number.isFinite(parsed) ? parsed : val);
						}}
						className="topbuddy-settings-field__input"
						__next40pxDefaultSize
						__nextHasNoMarginBottom
					/>
				</div>
			);

		case 'code':
			return (
				<div key={key} className="topbuddy-settings-field topbuddy-settings-field--code">
					<label className="topbuddy-settings-field__label">{field.label}</label>
					<textarea
						value={value || ''}
						onChange={(e) => handleSettingChange(key, e.target.value)}
						className="topbuddy-settings-field__code"
						rows={8}
						placeholder={field.placeholder || __('Enter HTML code...', 'topbuddy')}
					/>
				</div>
			);
			
		case 'select':
				// Transform PHP associative array to SelectControl format
				let selectOptions = [];
				if (field.options) {
					if (Array.isArray(field.options)) {
						// Check if it's an array of strings (simple values)
						if (typeof field.options[0] === 'string') {
							// Convert array of strings to {label, value} format
							selectOptions = field.options.map(option => ({
								label: option.toUpperCase(),
								value: option
							}));
						} else {
							// Already in {label, value} format
							selectOptions = field.options;
						}
					} else {
						// Convert object to array of {label, value} objects
						selectOptions = Object.keys(field.options).map(optionValue => ({
							label: field.options[optionValue],
							value: optionValue
						}));
					}
				}
				
				const isPro = field.pro || false;
				const isDisabled = isPro && !isProActive;
				
				return (
					<div key={key} className="topbuddy-settings-field">
						<label className="topbuddy-settings-field__label">
							{field.label}
							{isPro && !isProActive && (
								<span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginLeft: '6px' }}>
									<ProBadge />
									<Button
										variant="link"
										href={upgradeUrl}
										target="_blank"
										rel="noopener noreferrer"
										style={{ textDecoration: 'none', textTransform: 'none' }}
									>
										{__('Upgrade', 'topbuddy')}
										<span className="dashicons dashicons-external" style={{ marginLeft: '5px' }}></span>
									</Button>
								</span>
							)}
						</label>
						<div style={{ opacity: isDisabled ? 0.5 : 1, pointerEvents: isDisabled ? 'none' : 'auto' }}>
							<SelectControl
								value={value || field.default}
								options={selectOptions}
								onChange={(val) => handleSettingChange(key, val)}
								className="topbuddy-settings-field__select"
								disabled={isDisabled}
								__next40pxDefaultSize
								__nextHasNoMarginBottom
							/>
						</div>
					</div>
				);

			case 'checkbox':
				return (
					<div key={key} className="topbuddy-settings-field topbuddy-settings-field--checkbox">
						<ToggleControl
							label={field.label}
							checked={!!value}
							onChange={(val) => handleSettingChange(key, val)}
							__nextHasNoMarginBottom
						/>
					</div>
				);

			case 'radio':
				return (
					<div key={key} className="topbuddy-settings-field topbuddy-settings-field--radio">
						<RadioControl
							label={field.label}
							selected={value || field.default}
							options={Object.keys(field.options || {}).map(optionKey => ({
								label: field.options[optionKey],
								value: optionKey
							}))}
							onChange={(val) => handleSettingChange(key, val)}
						/>
					</div>
				);

			case 'color':
				return (
					<div key={key} className="topbuddy-settings-field topbuddy-settings-field--color">
						<label className="topbuddy-settings-field__label">{field.label}</label>
						<div className="topbuddy-settings-field__color-picker">
							<Dropdown
								className="topbuddy-color-dropdown"
								contentClassName="topbuddy-color-dropdown__content"
								popoverProps={{ placement: 'bottom-start' }}
								renderToggle={({ isOpen, onToggle }) => (
									<Button
										onClick={onToggle}
										aria-expanded={isOpen}
										className="topbuddy-color-dropdown__toggle"
									>
										<ColorIndicator colorValue={value || field.default || '#000000'} />
										<span className="topbuddy-color-dropdown__label">
											{value || field.default || '#000000'}
										</span>
									</Button>
								)}
								renderContent={() => (
									<ColorPicker
										color={value || field.default || '#000000'}
										onChange={(val) => handleSettingChange(key, val)}
										enableAlpha={false}
									/>
								)}
							/>
						</div>
					</div>
				);

			case 'box_control_lr':
				// BoxControl expects a full box value object; we only expose left/right.
				const boxDefaults = {
					top: '0px',
					right: '0px',
					bottom: '0px',
					left: '0px',
				};
				const boxValues = {
					...boxDefaults,
					...(typeof field.default === 'object' && field.default ? field.default : {}),
					...(typeof value === 'object' && value ? value : {}),
				};

				return (
					<div key={key} className="topbuddy-settings-field topbuddy-settings-field--box-control">
						<BoxControl
							label={field.label}
							values={boxValues}
							onChange={(val) => handleSettingChange(key, { ...boxValues, ...val })}
							sides={['left', 'right']}
							units={[
								{ value: 'px', label: 'px' },
								{ value: 'vw', label: 'vw' },
							]}
						/>
					</div>
				);

			case 'datetime':
				const localValue = toLocal(value);
				return (
					<div key={key} className="topbuddy-settings-field">
						<label className="topbuddy-settings-field__label">{field.label}</label>
						<Dropdown
							className="topbuddy-datetime-dropdown"
							contentClassName="topbuddy-datetime-dropdown__content"
							popoverProps={{ placement: 'bottom-start' }}
							renderToggle={({ isOpen, onToggle }) => (
								<Button
									onClick={onToggle}
									aria-expanded={isOpen}
									variant="secondary"
								>
									{localValue ? localValue.toLocaleString() : __('Select date & time', 'topbuddy')}
								</Button>
							)}
							renderContent={() => (
								<DateTimePicker
									currentDate={localValue}
									onChange={(date) => handleSettingChange(key, toSite(date))}
									is12Hour={false}
								/>
							)}
						/>
					</div>
				);

			case 'date':
				return (
					<div key={key} className="topbuddy-settings-field">
						<label className="topbuddy-settings-field__label">{field.label}</label>
						<input
							type="date"
							value={value || ''}
							onChange={(e) => handleSettingChange(key, e.target.value)}
							className="topbuddy-settings-field__input"
						/>
					</div>
				);

			case 'icon_radio':
				// Get icons from topbuddyPostSettings
				const availableIcons = window.topbuddyPostSettings?.icons || {};
				
				// Transform options if needed
				let iconOptions = [];
				if (field.options) {
					if (Array.isArray(field.options)) {
						// Convert array of icon names to objects with icon HTML
						iconOptions = field.options.map(iconName => ({
							value: iconName,
							label: iconName.charAt(0).toUpperCase() + iconName.slice(1),
							icon: availableIcons[iconName] || ''
						}));
					} else {
						// Already in object format
						iconOptions = Object.keys(field.options).map(iconValue => ({
							value: iconValue,
							label: field.options[iconValue],
							icon: availableIcons[iconValue] || ''
						}));
					}
				}
				
				return (
					<div key={key} className="topbuddy-settings-field">
						<label className="topbuddy-settings-field__label">{field.label}</label>
						<div className="topbuddy-icon-radio-group">
							{iconOptions.map((option) => (
								<label
									key={option.value}
									className={`topbuddy-icon-radio-option ${value === option.value ? 'is-selected' : ''}`}
								>
									<input
										type="radio"
										name={`${component.id}-${key}`}
										value={option.value}
										checked={value === option.value}
										onChange={() => handleSettingChange(key, option.value)}
									/>
									<div
										className="topbuddy-icon-radio-preview"
										dangerouslySetInnerHTML={{ __html: option.icon }}
									/>
								</label>
							))}
						</div>
					</div>
				);

			case 'image':
				let imageFrame;
				
				const handleImageUpload = (e) => {
					e.preventDefault();

					if (imageFrame) {
						imageFrame.open();
						return;
					}

					imageFrame = wp.media({
						title: __('Select or Upload Image', 'topbuddy'),
						button: {
							text: __('Use this image', 'topbuddy'),
						},
						multiple: false,
					});

					imageFrame.on('select', function () {
						const attachment = imageFrame.state().get('selection').first().toJSON();
						// Batch all changes together for immediate update
						const newSettings = {
							...settings,
							[key]: attachment.url,
							image_id: attachment.id,
							alt_text: attachment.alt || ''
						};
						setSettings(newSettings);
						onUpdateSettings(newSettings);
					});

					imageFrame.open();
				};

				const handleImageRemove = () => {
					// Batch all changes together for immediate update
					const newSettings = {
						...settings,
						[key]: '',
						image_id: '',
						alt_text: ''
					};
					setSettings(newSettings);
					onUpdateSettings(newSettings);
				};

				return (
					<div key={key} className="topbuddy-settings-field">
						<label className="topbuddy-settings-field__label">{field.label}</label>
						<div className="topbuddy-image-upload-field">
							{value ? (
								<>
									<img 
										src={value} 
										alt="" 
										onClick={handleImageUpload}
										style={{ 
											maxWidth: '200px', 
											display: 'block', 
											marginBottom: '10px',
											cursor: 'pointer',
											border: '2px solid transparent',
											borderRadius: '4px',
											transition: 'border-color 0.2s'
										}}
										onMouseOver={(e) => e.target.style.borderColor = '#007cba'}
										onMouseOut={(e) => e.target.style.borderColor = 'transparent'}
									/>
									<Button
										variant="secondary"
										isDestructive
										onClick={handleImageRemove}
									>
										{__('Remove Image', 'topbuddy')}
									</Button>
								</>
							) : (
								<Button
									variant="secondary"
									onClick={handleImageUpload}
								>
									{__('Upload Image', 'topbuddy')}
								</Button>
							)}
						</div>
					</div>
				);

			default:
				return null;
		}
	};

	const contentFields = {};
	const stylingFields = {};
	const styleFields = {};

	if (config.settings) {
		Object.keys(config.settings).forEach((key) => {
			const field = config.settings[key];
			// Check for both 'Styling' and 'Style' tabs
			if (field.tab === 'Styling' || field.tab === 'styling' || field.tab === 'Style') {
				stylingFields[key] = field;
				styleFields[key] = field;
			} else {
				contentFields[key] = field;
			}
		});
	}

	// Check if we have styling fields
	const hasStylingFields = Object.keys(stylingFields).length > 0 || Object.keys(styleFields).length > 0;

	const tabs = [
		{
			name: 'content',
			title: __('Content', 'topbuddy'),
			className: 'topbuddy-settings-tab-content',
		},
		{
			name: 'styling',
			title: __('Styling', 'topbuddy'),
			className: 'topbuddy-settings-tab-styling',
		},
	];

	return (
		<>
			<div className="topbuddy-component-settings-backdrop" onClick={handleBackdropClick} />
			<div className="topbuddy-component-settings" ref={modalRef} onClick={handleModalClick}>
				<div className="topbuddy-component-settings__arrow"></div>
				<div className="topbuddy-component-settings__header">
				<h4 className="topbuddy-component-settings__title">
					{config.name} {__('Settings', 'topbuddy')}
				</h4>
				<button
					type="button"
					className="topbuddy-component-settings__close"
					onClick={onClose}
					aria-label={__('Close', 'topbuddy')}
				>
					<span className="dashicons dashicons-no-alt"></span>
				</button>
			</div>

			<div className="topbuddy-component-settings__body">
				{hasStylingFields ? (
					<div className="topbuddy-component-settings__tabs">
						<TabPanel
							className="topbuddy-settings-tabs"
							activeClass="is-active"
							tabs={tabs}
							initialTabName="content"
						>
							{(tab) => (
								<div className="topbuddy-component-settings__tab-content">
									{tab.name === 'content' && (
										<>
											{Object.keys(contentFields).length > 0 ? (
												Object.keys(contentFields).map((key) =>
													renderField(key, contentFields[key])
												)
											) : (
												<p className="topbuddy-component-settings__empty">
													{__('No content settings available.', 'topbuddy')}
												</p>
											)}
										</>
									)}
									{tab.name === 'styling' && (
										<>
											{(Object.keys(stylingFields).length > 0 || Object.keys(styleFields).length > 0) ? (
												[...Object.keys(stylingFields), ...Object.keys(styleFields)]
													.filter((key, index, self) => self.indexOf(key) === index)
													.map((key) =>
														renderField(key, stylingFields[key] || styleFields[key])
													)
											) : (
												<p className="topbuddy-component-settings__empty">
													{__('No styling settings available.', 'topbuddy')}
												</p>
											)}
										</>
									)}
								</div>
							)}
						</TabPanel>
					</div>
				) : (
					<div className="topbuddy-component-settings__fields">
						{Object.keys(contentFields).length > 0 ? (
							Object.keys(contentFields).map((key) =>
								renderField(key, contentFields[key])
							)
						) : (
							<p className="topbuddy-component-settings__empty">
								{__('No settings available.', 'topbuddy')}
							</p>
						)}
					</div>
				)}
			</div>
			</div>
		</>
	);
};

export default ComponentSettings;
